import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	IconButton,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	MenuItem,
	MenuList,
	Paper,
	TextField,
	ThemeProvider,
	Typography,
} from '@mui/material';
import React, { memo, useEffect, useRef } from 'react';
import { useState } from 'react';
import ArrowDropDownSharpIcon from '@mui/icons-material/ArrowDropDownSharp';
import { theme } from '../../theme/theme';
import { Check, Close, ExpandMore, Search } from '@mui/icons-material';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { colors } from '../../theme/color-palette';
import { definitions } from './definitions';

const useStyles = makeStyles(() => ({
	inputPlaceholder: {
		'& .MuiInputBase-input::placeholder': {
			fontSize: 14,
		},
	},
}));

const MenuWithDefinition = memo((props) => {
	const { id, options, selectedOption, handleFilterChange } = props;
	const [currentHoverElement, setCurrentHoverElement] =
		useState(selectedOption);
	const [open, setOpen] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const classes = useStyles();

	const useOutsideAlerter = (ref) => {
		useEffect(() => {
			/**
			 * Alert if clicked on outside of element
			 */
			function handleClickOutside(event) {
				event.stopPropagation();
				if (ref.current && !ref.current.contains(event.target)) {
					setOpen(false);
					setCurrentHoverElement(selectedOption);
					setSearchValue('');
				}
			}
			// Bind the event listener

			document.addEventListener('click', handleClickOutside);
			return () => {
				// Unbind the event listener on clean up
				document.removeEventListener('click', handleClickOutside);
			};
		}, [ref, selectedOption]);
	};

	const filteredOptionsPrimary = options?.filter((option) =>
		searchValue !== ''
			? option.label.toLowerCase().includes(searchValue.toLowerCase())
			: option
	);

	const handleHover = (item) => {
		setCurrentHoverElement(item);
	};

	const wrapperRef = useRef(null);
	const buttonRef = useRef(null);

	useOutsideAlerter(wrapperRef);

	return (
		<>
			<ThemeProvider theme={theme}>
				<Box
					sx={{
						display: 'flex',
						gap: '6px',
					}}>
					<Button
						id={`${id}-dropdown`}
						ref={buttonRef}
						variant="text"
						onClick={(e) => {
							e.stopPropagation();
							setOpen(!open);
						}}
						sx={{
							textTransform: 'none !important',
							borderRadius: '100px',
							height: '32px',
							padding: '0 8px',
							color:
								selectedOption === '' ? colors.PURPLE_AI : '#2E2F32 !important',
							':hover': {
								backgroundColor: '#D5D6D8',
							},
							position: 'relative',
							display: 'flex',
							gap: '8px',
							justifyContent: 'flex-start',
							minWidth: 0,
							width: 'fit-content',
							// minWidth: 'none',
							// display: 'inline-block';
						}}
						endIcon={
							selectedOption === '' ? null : (
								<ArrowDropDownSharpIcon fontSize="small" />
							)
						}>
						<Typography
							sx={{
								lineHeight: '20px',
								fontWeight: 400,
								fontSize: '14px',
							}}>
							{selectedOption === '' ? 'Compare' : selectedOption}
						</Typography>
					</Button>
					{selectedOption !== '' && id === 'compare' && (
						<IconButton
							sx={{
								padding: '8px',
								height: '16px',
								width: '16px',
							}}
							onClick={() => handleFilterChange('')}>
							<Close fontSize="small" />
						</IconButton>
					)}
				</Box>

				<Box
					ref={wrapperRef}
					sx={{
						display: open ? 'flex' : 'none',
						gap: '4px',
						zIndex: 100,
						position: 'absolute',
						marginTop: `${buttonRef?.current?.offsetHeight + 4}px`,
						marginLeft: `10px`,
					}}>
					<Paper
						sx={{
							boxShadow:
								'0px -1px 3px 0px rgba(0, 0, 0, 0.10), 0px 3px 5px 2px rgba(0, 0, 0, 0.15)',
							borderRadius: '4px',
							width: '280px',
							height: 'fit-content',
						}}>
						<TextField
							id="outlined-basic"
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
							variant="outlined"
							size="small"
							sx={{
								padding: '8px 8px 8px 8px',
								width: '-webkit-fill-available',
								'& .MuiOutlinedInput-input': {
									paddingLeft: '0px',
								},
								'& .MuiOutlinedInput-root': {
									'&.Mui-focused fieldset': {
										border:
											'1px solid var(--Full-Color-Palette-Gray-Gray-30, #D5D6D8)',
									},
								},
							}}
							placeholder={'Search'}
							InputProps={{
								className: classes.inputPlaceholder,
								startAdornment: (
									<InputAdornment position="start">
										<Search />
									</InputAdornment>
								),
							}}
						/>
						<MenuList
							dense
							sx={{
								overflowY: 'scroll',
								maxHeight: '340px',
								overflow: 'auto',
								whiteSpace: 'nowrap',
								'& .MuiButtonBase-root': {
									display: 'flex',
								},
							}}>
							{filteredOptionsPrimary.length === 0 ? (
								<Box>
									<Typography
										sx={{
											fontSize: '14px',
											fontWeight: 400,
											lineHeight: '20px',
											color: colors.LIGHT_GRAY_HEX,
										}}>
										{'No matches found for this search'}
									</Typography>
								</Box>
							) : (
								filteredOptionsPrimary?.map((option) => (
									<MenuItem
										onMouseEnter={() => handleHover(option.label)}
										onClick={() => {
											handleFilterChange(option.label);
											setOpen(false);
										}}
										value={option.label}
										key={option.id}
										sx={{
											height: '36px',
											minHeight: '36px',
										}}>
										{selectedOption === option.label && (
											<ListItemIcon>
												<Check />
											</ListItemIcon>
										)}

										<ListItemText
											sx={{
												textAlign: 'start',
												'& .MuiTypography-root': {
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												},
											}}
											inset={selectedOption !== option.label}>
											{option.label}
										</ListItemText>
									</MenuItem>
								))
							)}
						</MenuList>
					</Paper>
					{currentHoverElement !== '' &&
						definitions[currentHoverElement] &&
						Object.keys(definitions[currentHoverElement]).length !== 0 && (
							<Paper
								sx={{
									padding: '16px',
									borderRadius: '4px',
									width: '280px',
									overflowY: 'auto',
									height: 'fit-content',
									boxShadow:
										'0px -1px 3px 0px rgba(0, 0, 0, 0.10), 0px 3px 5px 2px rgba(0, 0, 0, 0.15)',
								}}>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'start',
										gap: '12px',
									}}>
									<Box
										sx={{
											color: colors.DARK_GREY,
											display: 'flex',
											flexDirection: 'column',
											gap: '8px',
											justifyContent: 'flex-start',
										}}>
										<Typography
											sx={{
												lineHeight: '20px',
												fontSize: '14px',
												fontWeight: 700,
												textAlign: 'start',
											}}>
											{currentHoverElement}
										</Typography>
										<Typography
											sx={{
												lineHeight: '20px',
												fontSize: '14px',
												fontWeight: 400,
												textAlign: 'start',
											}}>
											{definitions[currentHoverElement]?.definition}
										</Typography>
									</Box>

									{Object.keys(definitions[currentHoverElement].aggregation)
										.length !== 0 && (
										<Accordion
											sx={{
												position: 'initial',
												boxShadow: 'none',
												'&.Mui-expanded': {
													margin: 0,
												},
											}}>
											<AccordionSummary
												expandIcon={<ExpandMore />}
												sx={{
													marginBottom: '1px',
													borderRadius: '4px',
													height: '32px',
													minHeight: '32px !important',
													backgroundColor: '#F8F8F8',
													'& .MuiAccordionSummary-content': {
														'& .Mui-expanded': {
															margin: 0,
														},
													},
												}}
												aria-controls="panel1-content"
												id="panel1-header">
												<Typography
													sx={{
														lineHeight: '16px',
														paddingRight: '8px',
														fontWeight: 700,
														color: '#515357',
														fontSize: '12px',
													}}>
													{definitions[currentHoverElement]?.aggregation?.label}
												</Typography>
											</AccordionSummary>
											<AccordionDetails
												sx={{
													backgroundColor: '#F8F8F8',
													padding: '8px',
													display: 'flex',
													flexDirection: 'column',
													gap: '5px',
												}}>
												<Typography
													sx={{
														textAlign: 'start',
														fontSize: '12px',
														lineHeight: '16px',
														paddingLeft: '8px',
														paddingRight: '8px',
														wordWrap: 'break-word',
													}}>
													{
														definitions[currentHoverElement]?.aggregation
															?.preList
													}
												</Typography>
												{definitions[currentHoverElement]?.aggregation?.list
													?.length !== 0 && (
													<ol
														style={{
															textAlign: 'start',
															fontSize: '12px',
															lineHeight: '16px',
															paddingLeft: '20px',
															paddingRight: '8px',
															wordWrap: 'break-word',
														}}>
														{definitions[
															currentHoverElement
														]?.aggregation?.list?.map((item, idx) => (
															<li key={idx}>
																<Typography
																	key={idx}
																	sx={{
																		textAlign: 'start',
																		fontSize: '12px',
																		lineHeight: '16px',
																	}}>
																	{item}
																</Typography>
															</li>
														))}
													</ol>
												)}

												<Typography
													sx={{
														textAlign: 'start',
														fontSize: '12px',
														lineHeight: '16px',
														paddingLeft: '8px',
														paddingRight: '8px',
														wordWrap: 'break-word',
													}}>
													{
														definitions[currentHoverElement]?.aggregation
															?.postList
													}
												</Typography>
											</AccordionDetails>
										</Accordion>
									)}
								</Box>
							</Paper>
						)}
				</Box>
			</ThemeProvider>
		</>
	);
});

MenuWithDefinition.propTypes = {
	id: PropTypes.string,
	options: PropTypes.arrayOf(PropTypes.shape({})),
	selectedOption: PropTypes.string,
	handleFilterChange: PropTypes.func,
};

MenuWithDefinition.displayName = 'MenuWithDefinition';

export default MenuWithDefinition;
